import { useState, useEffect, useMemo } from 'react';
import { callRpc } from '../../lib/rpcClient';
import { CreditCard, Calendar, User, MessageSquare, TrendingUp, ChevronDown, ChevronRight, Activity, Users, MessageCircle } from 'lucide-react';
import { format } from 'date-fns';

type ViewMode = 'activity' | 'users' | 'feedback';

export function AdminPricingStats() {
    const [stats, setStats] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('activity');
    const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
    
    const pricingStats = useMemo(() => {
        return stats.filter(s => 
            s.event_type !== 'car_tasks_opened' && 
            s.event_type !== 'car_tasks_pressed_get_started'
        );
    }, [stats]);

    const summary = useMemo(() => {
        const opened = pricingStats.filter(s => s.event_type === 'opened').length;
        
        const proUnique = new Set(pricingStats.filter(s => s.event_type === 'pressed_pro').map(s => s.user_id)).size;
        const customUnique = new Set(pricingStats.filter(s => s.event_type === 'pressed_custom').map(s => s.user_id)).size;
        const totalUnique = new Set(pricingStats.map(s => s.user_id)).size;

        return {
            total_opened: opened,
            unique_pro: proUnique,
            unique_custom: customUnique,
            unique_users: totalUnique
        };
    }, [pricingStats]);

    const groupedStats = useMemo(() => {
        const groups: Record<string, { 
            user_id: string, 
            email: string, 
            events: any[], 
            counts: Record<string, number>,
            last_activity: string 
        }> = {};

        pricingStats.forEach(s => {
            if (!groups[s.user_id]) {
                groups[s.user_id] = {
                    user_id: s.user_id,
                    email: s.user_email || 'Anonymous',
                    events: [],
                    counts: { 
                        opened: 0, 
                        pressed_pro: 0, 
                        pressed_custom: 0
                    },
                    last_activity: s.created_at
                };
            }
            if (s.event_type !== 'opened' && s.event_type !== 'pressed_pro' && s.event_type !== 'pressed_custom') {
                return;
            }
            groups[s.user_id].events.push(s);
            groups[s.user_id].counts[s.event_type] = (groups[s.user_id].counts[s.event_type] || 0) + 1;
            if (new Date(s.created_at) > new Date(groups[s.user_id].last_activity)) {
                groups[s.user_id].last_activity = s.created_at;
            }
        });

        return Object.values(groups).sort((a, b) => new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime());
    }, [pricingStats]);

    const feedbackStats = useMemo(() => {
        return pricingStats.filter(s => s.feedback && s.feedback.trim().length > 0);
    }, [pricingStats]);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await callRpc('get_admin_pricing_stats');
            setStats(data || []);
        } catch (err: any) {
            console.error('Error fetching pricing stats:', err);
            setError(err.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const toggleUser = (userId: string) => {
        const next = new Set(expandedUsers);
        if (next.has(userId)) next.delete(userId);
        else next.add(userId);
        setExpandedUsers(next);
    };

    const getEventBadge = (type: string) => {
        switch (type) {
            case 'opened':
                return <span className="bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest leading-none border border-slate-700">Opened</span>;
            case 'pressed_pro':
                return <span className="bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest leading-none border border-blue-500/20">Pro Upgrade</span>;
            case 'pressed_custom':
                return <span className="bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest leading-none border border-purple-500/20">Custom Plan</span>;
            default:
                return <span className="bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest leading-none border border-slate-700">{type?.replace('_', ' ')}</span>;
        }
    };

    return (
        <div className="space-y-8 font-primary pb-20">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-black text-white mb-2">Pricing Insights</h1>
                    <p className="text-slate-500 font-medium italic">Monitor conversions and user feedback for premium tiers</p>
                </div>
                <button 
                    onClick={fetchStats}
                    className="p-3 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-all text-slate-400 hover:text-white"
                >
                    <TrendingUp size={20} className={loading ? 'animate-spin' : ''} />
                </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-500/10 rounded-xl">
                            <Calendar size={18} className="text-blue-400" />
                        </div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Total Page Views</span>
                    </div>
                    <div className="text-3xl font-black text-white">{summary.total_opened}</div>
                </div>
                <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-emerald-500/10 rounded-xl">
                            <CreditCard size={18} className="text-emerald-400" />
                        </div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Pro Interested (Unique)</span>
                    </div>
                    <div className="text-3xl font-black text-white">{summary.unique_pro}</div>
                </div>
                <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-purple-500/10 rounded-xl">
                            <MessageSquare size={18} className="text-purple-400" />
                        </div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Custom Interested (Unique)</span>
                    </div>
                    <div className="text-3xl font-black text-white">{summary.unique_custom}</div>
                </div>
                <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-orange-500/10 rounded-xl">
                            <User size={18} className="text-orange-400" />
                        </div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Unique Users Total</span>
                    </div>
                    <div className="text-3xl font-black text-white">{summary.unique_users}</div>
                </div>
            </div>

            {/* View Selectors */}
            <div className="flex p-1.5 bg-slate-900 border border-slate-800 rounded-2xl w-fit">
                {[
                    { id: 'activity', label: 'All Activity', icon: Activity },
                    { id: 'users', label: 'Group by User', icon: Users },
                    { id: 'feedback', label: 'Feedback Only', icon: MessageCircle }
                ].map(mode => (
                    <button
                        key={mode.id}
                        onClick={() => setViewMode(mode.id as ViewMode)}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${viewMode === mode.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}`}
                    >
                        <mode.icon size={14} />
                        {mode.label}
                    </button>
                ))}
            </div>

            {/* Content Container */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
                {loading ? (
                    <div className="p-20 flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                ) : error ? (
                    <div className="p-16 text-center">
                        <div className="text-red-400 font-bold mb-2">Error loading data</div>
                        <div className="text-slate-500 text-sm italic">{error}</div>
                    </div>
                ) : (
                    <>
                        {/* 1. All Activity View */}
                        {viewMode === 'activity' && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-800 bg-slate-800/30 text-slate-500 text-[10px] font-black uppercase tracking-widest">
                                            <th className="px-6 py-4">When</th>
                                            <th className="px-6 py-4">User</th>
                                            <th className="px-6 py-4">Action</th>
                                            <th className="px-6 py-4">Info/Feedback</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800">
                                        {pricingStats.map((item) => (
                                            <tr key={item.id} className="hover:bg-white/5 transition-colors group">
                                                <td className="px-6 py-4 text-xs text-slate-400 whitespace-nowrap">
                                                    {format(new Date(item.created_at), 'dd MMM, HH:mm')}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col">
                                                        <span className="text-white font-bold text-sm truncate max-w-[180px]">{item.user_email || 'Anonymous'}</span>
                                                        <span className="text-[9px] text-slate-600 font-mono">{item.user_id.split('-')[0]}...</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {getEventBadge(item.event_type)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="max-w-md">
                                                        {item.feedback ? (
                                                            <div className="text-sm text-slate-300 leading-relaxed border-l-2 border-blue-500/30 pl-4 py-1 italic">
                                                                {item.feedback}
                                                            </div>
                                                        ) : item.billing_cycle ? (
                                                            <span className="text-xs text-slate-600 font-bold uppercase tracking-widest">Plan: {item.billing_cycle}</span>
                                                        ) : (
                                                            <span className="text-slate-800 italic text-xs">No feedback</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* 2. Grouped Users View */}
                        {viewMode === 'users' && (
                            <div className="divide-y divide-slate-800">
                                {groupedStats.map((group) => {
                                    const isExpanded = expandedUsers.has(group.user_id);
                                    return (
                                        <div key={group.user_id} className="group">
                                            <button 
                                                onClick={() => toggleUser(group.user_id)}
                                                className="w-full h-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors text-left"
                                            >
                                                <div className="flex items-center gap-6">
                                                    <div className={`p-3 rounded-2xl ${isExpanded ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400'}`}>
                                                        {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                                    </div>
                                                    <div>
                                                        <div className="text-white font-black text-lg mb-1">{group.email}</div>
                                                        <div className="flex items-center gap-4">
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Total Actions: <span className="text-slate-400">{group.events.length}</span></span>
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Last Seen: <span className="text-slate-400">{format(new Date(group.last_activity), 'dd MMM, HH:mm')}</span></span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    {group.counts.pressed_pro > 0 && <span className="px-3 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-[10px] font-black uppercase border border-emerald-500/20">Pro x{group.counts.pressed_pro}</span>}
                                                    {group.counts.pressed_custom > 0 && <span className="px-3 py-1 rounded-lg bg-orange-500/10 text-orange-400 text-[10px] font-black uppercase border border-orange-500/20">Custom x{group.counts.pressed_custom}</span>}
                                                </div>
                                            </button>
                                            
                                            {isExpanded && (
                                                <div className="px-12 pb-8 pt-2 space-y-4 bg-slate-950/30">
                                                    <div className="flex items-center gap-2 mb-4">
                                                        <div className="w-8 h-px bg-slate-800"></div>
                                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Event History</span>
                                                        <div className="flex-1 h-px bg-slate-800"></div>
                                                    </div>
                                                    <div className="space-y-3">
                                                        {group.events.map((event: any) => (
                                                            <div key={event.id} className="flex items-start justify-between gap-6 p-4 rounded-2xl bg-white/5 border border-white/5">
                                                                <div className="flex items-start gap-4">
                                                                    <div className="mt-1">{getEventBadge(event.event_type)}</div>
                                                                    <div className="flex-1">
                                                                        {event.feedback ? (
                                                                            <p className="text-sm text-slate-300 italic">"{event.feedback}"</p>
                                                                        ) : (
                                                                            <p className="text-xs text-slate-500">{event.event_type === 'opened' ? 'Viewed pricing page' : `Interested in ${event.event_type.replace('pressed_', '')} - ${event.billing_cycle || 'not specified'}`}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <span className="text-[10px] font-bold text-slate-600 whitespace-nowrap">{format(new Date(event.created_at), 'MM/dd HH:mm')}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* 3. Feedback Only View */}
                        {viewMode === 'feedback' && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-800 bg-slate-800/30 text-slate-500 text-[10px] font-black uppercase tracking-widest">
                                            <th className="px-6 py-4">Customer</th>
                                            <th className="px-6 py-4">Context</th>
                                            <th className="px-6 py-4">Feedback Message</th>
                                            <th className="px-6 py-4">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800">
                                        {feedbackStats.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="p-20 text-center text-slate-600 font-bold italic">No feedback messages collected yet</td>
                                            </tr>
                                        ) : (
                                            feedbackStats.map((item) => (
                                                <tr key={item.id} className="hover:bg-white/5 transition-colors">
                                                    <td className="px-6 py-6">
                                                        <div className="text-white font-bold text-sm mb-1">{item.user_email || 'Anonymous'}</div>
                                                        <div className="text-[9px] text-slate-600 font-mono">{item.user_id}</div>
                                                    </td>
                                                    <td className="px-6 py-6">
                                                        <div className="flex flex-col gap-1.5">
                                                            {getEventBadge(item.event_type)}
                                                            {item.billing_cycle && <span className="text-[10px] font-black text-slate-500 uppercase">{item.billing_cycle}</span>}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-6">
                                                        <div className="relative">
                                                            <div className="absolute -left-4 top-0 bottom-0 w-1 bg-blue-500 rounded-full opacity-30 shadow-sm shadow-blue-500/50"></div>
                                                            <p className="text-blue-50/90 text-[15px] font-medium leading-relaxed font-secondary italic tracking-tight">
                                                                "{item.feedback}"
                                                            </p>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-6 text-xs text-slate-500 whitespace-nowrap font-bold">
                                                        {format(new Date(item.created_at), 'dd MMM yyyy')}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
